import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";

type Props = {
  onScroll: () => void;
};

const menuItems = [
  { to: "/about", label: "About Me" },
  { to: "/experience", label: "Experience" },
  { to: "/certifications", label: "Certifications" },
  { to: "/contact", label: "Contact" },
];

const letterAnimation = {
  hidden: { opacity: 0, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05 },
  }),
};

const IntroMenu = ({ onScroll }: Props) => {
  const heading =
    "Software engineering with calm execution and sharp systems thinking.";
  const heroBackground = {
    background:
      "radial-gradient(circle at top left, color-mix(in srgb, var(--accent) 16%, transparent), transparent 28%), radial-gradient(circle at 88% 20%, color-mix(in srgb, var(--primary) 22%, transparent), transparent 22%), linear-gradient(155deg, color-mix(in srgb, var(--bg) 55%, black), color-mix(in srgb, var(--bg) 38%, black) 48%, color-mix(in srgb, var(--bg) 18%, black) 100%)",
  };
  const heroFrame = {
    borderColor: "var(--surface-border)",
    background:
      "linear-gradient(160deg, color-mix(in srgb, var(--surface-strong) 12%, transparent), color-mix(in srgb, var(--surface) 8%, transparent))",
    boxShadow: "0 30px 120px rgba(0,0,0,0.28)",
  };
  const heroLink = {
    borderColor: "var(--surface-border)",
    background: "color-mix(in srgb, var(--surface-strong) 10%, transparent)",
  };
  const pillStyle = {
    borderColor: "var(--surface-border)",
    background: "color-mix(in srgb, var(--surface-strong) 8%, transparent)",
  };
  const overlayCard = {
    borderColor: "var(--surface-border)",
    background: "color-mix(in srgb, black 36%, transparent)",
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="relative flex min-h-[100svh] w-full items-start overflow-hidden px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(2rem,env(safe-area-inset-bottom))] text-left sm:px-8 sm:pt-[max(1.5rem,env(safe-area-inset-top))] sm:pb-[max(6.5rem,env(safe-area-inset-bottom))] md:px-12"
    >
      <div className="absolute inset-0" style={heroBackground} />
      <div
        className="absolute inset-x-3 inset-y-3 rounded-[1.6rem] backdrop-blur-[2px] sm:inset-x-8 sm:inset-y-6 sm:rounded-[2rem] md:inset-x-12 md:rounded-[2.5rem]"
        style={heroFrame}
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl sm:hidden">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-start justify-between gap-4"
        >
          <div className="min-w-0 flex-1">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-[color:var(--accent)]">
              Full Stack Engineer
            </p>
            <p className="mt-1 text-[0.65rem] font-medium uppercase tracking-[0.18em] text-[color:var(--text-soft)]">
              AWS Certified Developer
            </p>
          </div>

          <div className="relative w-16 shrink-0">
            <div
              className="absolute -inset-1 rounded-[1rem] blur-2xl"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in srgb, var(--accent) 28%, transparent), transparent 55%)",
              }}
            />
            <div
              className="relative overflow-hidden rounded-[1rem] border p-1 shadow-[0_18px_40px_rgba(0,0,0,0.2)] backdrop-blur-xl"
              style={heroFrame}
            >
              <div
                className="overflow-hidden rounded-[0.8rem] border"
                style={{ borderColor: "var(--surface-border)" }}
              >
                <img
                  src="/profile.jpg"
                  alt="Amr El Shimy"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="mt-5"
        >
          <h1 className="max-w-[13.5rem] text-[2rem] font-semibold leading-[1.02] text-[color:var(--text-strong)]">
            {heading.split(" ").map((word, i) => (
              <motion.span
                key={i}
                custom={i}
                variants={letterAnimation}
                initial="hidden"
                animate="visible"
                className="mr-2 inline-block"
              >
                {word}
              </motion.span>
            ))}
          </h1>

          <p className="mt-4 max-w-md text-sm leading-7 text-[color:var(--text-muted)]">
            I build dependable platforms, thoughtful developer experiences, and
            product-ready systems that scale without losing clarity.
          </p>
        </motion.div>

        <motion.nav
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.08,
              },
            },
          }}
          className="mt-6 grid gap-2.5"
        >
          {menuItems.map(({ to, label }) => (
            <motion.div
              key={to}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: { opacity: 1, y: 0 },
              }}
            >
              <Link
                to={to}
                className="group flex items-center justify-between rounded-[1.2rem] border px-4 py-3.5 text-sm font-medium text-[color:var(--text)] backdrop-blur-xl transition duration-300 hover:border-[color:var(--accent)]/55 hover:bg-white/10"
                style={heroLink}
              >
                <span className="text-[color:var(--text-muted)]">{label}</span>
                <span className="text-[color:var(--accent)] transition group-hover:translate-x-1">
                  ↗
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.nav>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.65 }}
          className="mt-5 flex flex-wrap gap-2 text-xs text-[color:var(--text-soft)]"
        >
          <span className="rounded-full border px-3 py-1.5" style={pillStyle}>
            Node.js
          </span>
          <span className="rounded-full border px-3 py-1.5" style={pillStyle}>
            TypeScript
          </span>
          <span className="rounded-full border px-3 py-1.5" style={pillStyle}>
            GraphQL
          </span>
          <span className="rounded-full border px-3 py-1.5" style={pillStyle}>
            Cloud & DevOps
          </span>
        </motion.div>

        <motion.button
          onClick={onScroll}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.65 }}
          className="relative z-20 mt-6 flex w-full items-center justify-center gap-3 rounded-full border px-5 py-3 text-sm font-semibold text-[#0b1514] shadow-[0_18px_55px_rgba(0,0,0,0.28)] transition hover:brightness-105"
          style={{
            borderColor: "color-mix(in srgb, var(--accent) 50%, transparent)",
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--accent) 96%, white), color-mix(in srgb, var(--accent-strong) 92%, transparent))",
          }}
          aria-label="Scroll to slides"
        >
          <span>Enter the story</span>
          <ChevronDown className="w-6 h-6" />
        </motion.button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="relative z-10 mx-auto hidden w-full max-w-6xl items-start gap-10 py-4 sm:grid lg:grid-cols-[1.1fr,0.9fr]"
      >
        <div className="order-1 flex flex-col justify-start gap-6 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xs font-semibold uppercase tracking-[0.35em] text-[color:var(--accent)]"
          >
            Full Stack Engineer • AWS Certified Developer
          </motion.p>

          <motion.h1 className="max-w-3xl text-5xl font-semibold leading-[1.05] text-[color:var(--text-strong)] lg:text-6xl">
            {heading.split(" ").map((word, i) => (
              <motion.span
                key={i}
                custom={i}
                variants={letterAnimation}
                initial="hidden"
                animate="visible"
                className="mr-3 inline-block"
              >
                {word}
              </motion.span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.65 }}
            className="max-w-2xl text-lg leading-8 text-[color:var(--text-muted)]"
          >
            I build dependable platforms, thoughtful developer experiences, and
            product-ready systems that scale without losing clarity.
          </motion.p>

          <motion.nav
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.08,
                },
              },
            }}
            className="grid max-w-2xl gap-3 sm:grid-cols-2"
          >
            {menuItems.map(({ to, label }) => (
              <motion.div
                key={to}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <Link
                  to={to}
                  className="group flex items-center justify-between rounded-[1.4rem] border px-5 py-4 text-base font-medium text-[color:var(--text)] backdrop-blur-xl transition duration-300 hover:border-[color:var(--accent)]/55 hover:bg-white/10"
                  style={heroLink}
                >
                  <span className="text-[color:var(--text-muted)]">
                    {label}
                  </span>
                  <span className="text-[color:var(--accent)] transition group-hover:translate-x-1">
                    ↗
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.nav>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.65 }}
            className="flex flex-wrap gap-3 text-sm text-[color:var(--text-soft)]"
          >
            <span className="rounded-full border px-4 py-2" style={pillStyle}>
              Node.js
            </span>
            <span className="rounded-full border px-4 py-2" style={pillStyle}>
              TypeScript
            </span>
            <span className="rounded-full border px-4 py-2" style={pillStyle}>
              GraphQL
            </span>
            <span className="rounded-full border px-4 py-2" style={pillStyle}>
              Cloud & DevOps
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="order-2 flex items-center justify-center pt-1 lg:justify-end"
        >
          <div className="relative w-full max-w-[23rem] md:max-w-[27rem]">
            <div
              className="absolute -inset-6 rounded-[2rem] blur-2xl"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in srgb, var(--accent) 28%, transparent), transparent 55%)",
              }}
            />
            <div
              className="relative overflow-hidden rounded-[2rem] border p-3 shadow-[0_30px_90px_rgba(0,0,0,0.4)] backdrop-blur-xl"
              style={heroFrame}
            >
              <div
                className="overflow-hidden rounded-[1.5rem] border"
                style={{ borderColor: "var(--surface-border)" }}
              >
                <img
                  src="/profile.jpg"
                  alt="Amr El Shimy"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
              <div
                className="absolute inset-x-8 bottom-8 rounded-[1.4rem] border px-4 py-4 text-[color:var(--text)] backdrop-blur-md"
                style={overlayCard}
              >
                <p className="text-xs uppercase tracking-[0.28em] text-[color:var(--accent)]">
                  Focused on reliable delivery
                </p>
                <p className="mt-2 text-sm leading-6 text-[color:var(--text-muted)]">
                  Clean architecture, strong execution, and product-minded
                  engineering.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={onScroll}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="relative z-20 mt-4 hidden items-center justify-center gap-3 rounded-full border px-5 py-3 text-sm font-semibold text-[#0b1514] shadow-[0_18px_55px_rgba(0,0,0,0.28)] transition hover:brightness-105 sm:absolute sm:bottom-5 sm:left-1/2 sm:flex sm:w-auto sm:-translate-x-1/2"
        style={{
          borderColor: "color-mix(in srgb, var(--accent) 50%, transparent)",
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--accent) 96%, white), color-mix(in srgb, var(--accent-strong) 92%, transparent))",
        }}
        aria-label="Scroll to slides"
      >
        <span>Enter the story</span>
        <ChevronDown className="w-8 h-8 animate-bounce" />
      </motion.button>
    </motion.section>
  );
};

export default IntroMenu;
